import React, { useCallback, useMemo, useState, useEffect } from 'react'
import { useSelector, useDispatch } from 'react-redux'
import { Trash, ArrowRight3, ArrowDown, ArrowUp3 } from '@refera/ui-icons'
import Theme from '@refera/ui-core'
import { Tooltip } from '@material-ui/core'
import { Grid, TextField, Typography, Collapse } from '@mui/material'
import { useFormContext, Controller, useFieldArray, useWatch } from 'react-hook-form'

import IconButton from '_/components/svg/icon-button'
import DragAndDrop from '_/components/common/drag-and-drop'
import Svg from '_/components/svg'
import InfoCircleIcon from '_assets/icons/ic-info-circle.svg'
import { AiSuggestionsModal } from './ai-suggestions-modal'

import {
  findProblemItemByField,
  findMaterialItemByField,
  handleWarrantyMonths,
  createFormDataBudgetResolution,
} from '_/views/budget-view-v2/utils/functions'
import Accordion from '_/views/finance/components/Accordion'
import { useBudgetForm } from '_/views/budget-view-v2/hooks/use-budget-form'
import {
  PROBLEMS_TABLE_ITEMS,
  MATERIAL_TABLE_ITEMS,
  AI_SUGGESTIONS_MODAL_INITIAL_STATE,
} from '_/views/budget-view-v2/utils/constants'

import {
  formatMoney,
  displayCurrency,
  getMaterialsInput,
  mergeAiSuggestions,
} from '_/utils/helpers'
import { TypeChip } from './type-chip'
import { useDialog } from '_/hooks/use-dialog'
import { AddServiceButton } from './add-service-button'
import { CanDoButtonAction } from '_/utils/can-do-button-action'
import useRolePermission from '_/hooks/use-role-permission'
import { BUTTONS_ACTION } from '_/utils/constants/service-order'

import useStyles from './styles'
import { useToast } from '_/hooks/use-toast'
import { getAiResponse, updateResolutionAiSuggestion } from '_/modules/budget/actions'
import { getStepStatusLogSelector } from '_/modules/budget/selectors'
import { currentServiceOrderSelector } from '_modules/service-orders/selectors'
import { useParams } from '@reach/router'
import { PERMISSIONS, PERMISSIONS_ACTIONS } from '_/utils/constants/permissions'
import { NumericInput } from '_/views/budget-view-v2/components/Inputs/NumericInput'
import { ErrorMessage } from '_/components/inputs/Input'
import { formatErrorMessage } from '_/views/finance/utils/FormatErrorMessage'
import AutoFixHighIcon from '@mui/icons-material/AutoFixHigh'
import classNames from 'classnames'
import { List, Map } from 'immutable'

const PriceConferenceTooltipContent = ({ data }) => {
  const styles = useStyles()

  if (!data?.length) return null

  const LABEL_MAP = {
    labor: 'Mão de obra',
    material: 'Materiais',
    total: 'Total',
  }

  return (
    <div className={styles.tooltipContent}>
      {data.map(item => (
        <div>
          <div className={styles.tooltipHeader}>
            <div>Código: {item.serviceExternalId}</div>
          </div>
          {item.values &&
            ['labor', 'material', 'total']
              .filter(key => item.values[key])
              .map(key => {
                const value = item.values[key]
                return (
                  <div key={key} className={styles.tooltipRow}>
                    <span style={{ textTransform: 'capitalize', whiteSpace: 'nowrap' }}>
                      {LABEL_MAP[key] || key}:
                    </span>
                    <span style={{ whiteSpace: 'nowrap' }}>
                      Valor: {formatMoney(value.value)} | Ideal: {formatMoney(value.ideal)} | Max:{' '}
                      {formatMoney(value.max)}
                    </span>
                  </div>
                )
              })}
        </div>
      ))}
    </div>
  )
}

export function ProblemsGridAccordion() {
  const styles = useStyles()
  const dispatch = useDispatch()
  const { closeDialog, showDialog } = useDialog()
  const { showToast } = useToast()

  const { setPage, isEditing, budget, setAddProblemReadOnly, setBudgetResolutionToEdit } =
    useBudgetForm()

  const {
    setValue,
    watch,
    getValues,
    control,
    formState: { isDirty, errors },
  } = useFormContext()

  const { fields, remove, move } = useFieldArray({ control, name: 'budgetResolutions' })

  const watchedField = field => {
    return watch(field)
  }

  const { isTradesman, isAdmin, isSAAS, checkUserPermission } = useRolePermission()
  const { budgetId } = useParams()
  const serviceOrder = useSelector(currentServiceOrderSelector)

  const budgetResolutions = useWatch({ name: 'budgetResolutions', control })

  const totalValue = useMemo(() => {
    return (budgetResolutions ?? []).map(r => Number(r?.total) || 0).reduce((acc, t) => acc + t, 0)
  }, [budgetResolutions])

  const isTradesmanProposeView = isTradesman && !isEditing && budget?.proposeOpened

  const tradesmanDetailItems = useMemo(() => {
    if (!isTradesmanProposeView) return []

    const rawItems = budget?.budgetItems ?? []
    const items = List.isList(rawItems) ? rawItems.toJS() : rawItems
    if (!Array.isArray(items) || !items.length) return []

    return items
      .slice()
      .sort((a, b) => (a?.id ?? 0) - (b?.id ?? 0))
      .map((item, index) => {
        const plain = Map.isMap(item) ? item.toJS() : item
        const id = `item-${plain?.id ?? index}-${index}`
        return {
          id,
          description: plain?.description ?? '',
          price: Number(plain?.price) || 0,
        }
      })
  }, [isTradesmanProposeView, budget])

  const displayTotalValue = useMemo(() => {
    if (isTradesmanProposeView && budget?.totalPriceTradesman !== undefined) {
      const value = Number(budget.totalPriceTradesman)
      return isNaN(value) ? totalValue : value
    }
    return totalValue
  }, [isTradesmanProposeView, budget, totalValue])

  const toggleMaterial = useCallback(
    id => {
      const current = getValues('__openMaterials') || {}

      setValue(
        '__openMaterials',
        {
          ...current,
          [id]: !current?.[id],
        },
        { shouldDirty: false }
      )
    },
    [getValues, setValue]
  )

  useEffect(() => {
    setValue('__aiLoading', false, { shouldDirty: false })
    setValue('__openMaterials', {}, { shouldDirty: false })
  }, [])

  const stepStatusLog = useSelector(getStepStatusLogSelector)
  const hasSomeBudgetInStepLog = useMemo(
    () => stepStatusLog.some(log => log.budgetId?.toString() === budgetId),
    [stepStatusLog, budgetId]
  )
  const canReferaEdit = isAdmin && !hasSomeBudgetInStepLog

  const editButtonAction = isTradesman
    ? BUTTONS_ACTION.EDIT_BUDGET_BY_PROVIDER
    : BUTTONS_ACTION.EDIT_BUDGET

  const canEditBudget = CanDoButtonAction({
    nameButton: editButtonAction,
  })

  const allowsEdition = canEditBudget || canReferaEdit

  const [aiSuggestionsModalInfo, setAiSuggestionsModalInfo] = useState(
    AI_SUGGESTIONS_MODAL_INITIAL_STATE
  )

  function handleAddServiceClick() {
    if (!budget?.id || isDirty) {
      return showDialog({
        type: 'warning',
        subject: 'Atenção',
        description: 'Salve antes as edições deste registro.',
        onApprove: closeDialog,
        labelApprove: 'Ok, entendi',
      })
    }

    if (!allowsEdition) {
      return showToast({
        type: 'info',
        message: 'O orçamento já está em uma etapa que não permite mais a sua edição.',
      })
    }

    if (fields.length === 50) {
      return showToast({
        type: 'warning',
        message: 'Você só pode inserir até 50 problemas por orçamento.',
      })
    }

    return setPage('add-service')
  }

  const updateRowPosition = useCallback(
    (startIndex, endIndex) => {
      move(startIndex, endIndex)
    },
    [move]
  )

  const listProps = useMemo(
    () => ({
      item: true,
      xs: 12,
      classes: { item: styles.fieldList },
      style: { gap: '2.4rem' },
    }),
    [styles.fieldList]
  )

  const itemProps = useMemo(() => ({
    item: true,
    xs: 12,
    columnSpacing: '24',
    wrap: 'nowrap',
  }))

  useEffect(() => {
    const ids = fields?.map(f => f.id).join(',')
    setValue('budgetResolutionIdsToKeep', ids)
  }, [fields])

  const shouldShowAiAdjustButton = useMemo(() => {
    if (isAdmin) return true

    const agencyAllowsAI = serviceOrder?.agency?.get('aiTextCorrection') ?? false
    if (isSAAS) return agencyAllowsAI

    return false
  }, [serviceOrder?.agency, isAdmin, isSAAS])

  function RemoveProblemButton({ resolutionIndex }) {
    function removeProblem() {
      remove(resolutionIndex)

      closeDialog()
    }

    function handleClick() {
      if (!allowsEdition) {
        return showToast({
          type: 'info',
          message: 'O orçamento já está em uma etapa que não permite mais a sua edição.',
        })
      }
      showDialog({
        type: 'warning',
        subject: 'Remover problema',
        description: 'Você tem certeza que deseja remover o problema?',
        onApprove: removeProblem,
        onCancel: closeDialog,
        labelApprove: 'Confirmar',
        labelCancel: 'Cancelar',
      })
      return null
    }

    return (
      <Tooltip title="Excluir problema" placement="top">
        <IconButton
          variant="ghost"
          onClick={() =>
            checkUserPermission({
              permission: PERMISSIONS.BUDGETS,
              permissionAction: [PERMISSIONS_ACTIONS.ADD, PERMISSIONS_ACTIONS.EDIT],
              functionAccess: handleClick,
            })
          }
          onMouseDown={e => e.stopPropagation()}
        >
          <Trash color="red" fontSize={20} />
        </IconButton>
      </Tooltip>
    )
  }

  function EditProblemButton({ item }) {
    function handleClick() {
      if (!allowsEdition) {
        return showToast({
          type: 'info',
          message: 'O orçamento já está em uma etapa que não permite mais a sua edição.',
        })
      }
      setBudgetResolutionToEdit(item)
      setPage('add-service')
      return null
    }

    return (
      <Tooltip title="Editar problema" placement="top">
        <IconButton
          variant="ghost"
          onClick={() =>
            checkUserPermission({
              permission: PERMISSIONS.BUDGETS,
              permissionAction: [PERMISSIONS_ACTIONS.ADD, PERMISSIONS_ACTIONS.EDIT],
              functionAccess: handleClick,
            })
          }
          onMouseDown={e => e.stopPropagation()}
        >
          <ArrowRight3 color="#1671ED" fontSize={20} />
        </IconButton>
      </Tooltip>
    )
  }

  function ExpandMaterialButton({ open, item }) {
    return (
      <Tooltip title="Materiais" placement="top">
        <IconButton onClick={() => toggleMaterial(item?.id)} onMouseDown={e => e.stopPropagation()}>
          {open ? (
            <ArrowUp3 color="#1671ED" fontSize={20} />
          ) : (
            <ArrowDown color="#1671ED" fontSize={20} />
          )}
        </IconButton>
      </Tooltip>
    )
  }

  const isBudgetValid = useMemo(() => {
    return budget?.proposeOpened || !budget?.isRevised
  }, [budget?.proposeOpened, budget?.isRevised])

  const validateInfoToAdjust = useCallback(
    (problemTitle, serviceDescription) => {
      if (!problemTitle || !serviceDescription) {
        showToast({
          type: 'error',
          message: 'Preencha Título do serviço e Solução para o problema.',
        })
        return false
      }

      if (!isBudgetValid) {
        const message = budget?.isRevised
          ? 'O orçamento já foi revisado.'
          : 'Não é possível ajustar este orçamento.'
        showToast({ type: 'error', message })
        return false
      }
      return true
    },
    [showToast, isBudgetValid, budget?.isRevised]
  )

  const handleAiAdjust = useCallback(
    async resolution => {
      if (!resolution) return

      const {
        problemText: problemTitle,
        serviceDescription,
        problemDescription,
        materials = [],
      } = resolution

      if (!validateInfoToAdjust(problemTitle, serviceDescription)) return

      setValue('__aiLoading', true, { shouldDirty: false })

      try {
        const input = { problem: problemTitle, serviceDescription, problemDescription }
        const materialsToValidate = getMaterialsInput(materials)

        const promises = [
          dispatch(getAiResponse({ modelSlug: 'correct_and_improve_agent', input })),
        ]

        if (materialsToValidate?.mapped.length > 0) {
          const materialsInput = materialsToValidate?.input
          promises.push(
            dispatch(
              getAiResponse({ modelSlug: 'spelling_correction_agent', input: materialsInput })
            )
          )
        }

        const [correctionResponse, spellingResponse] = await Promise.all(promises)

        const aiSuggestions = mergeAiSuggestions({
          existingAiSuggestions: resolution?.aiSuggestions,
          correctionResponse: correctionResponse?.response,
          spellingResponse: spellingResponse?.response,
        })

        const formData = createFormDataBudgetResolution({ aiSuggestions })
        await dispatch(updateResolutionAiSuggestion(resolution?.id, formData))

        const newServiceDescription = correctionResponse?.response?.serviceDescription?.trim() ?? ''
        const newProblemDescription = correctionResponse?.response?.problemDescription?.trim() ?? ''
        const newMaterialsDescriptions = spellingResponse?.response ?? []

        setAiSuggestionsModalInfo({
          open: true,
          resolution,
          serviceDescription: newServiceDescription,
          problemDescription: newProblemDescription,
          materials: newMaterialsDescriptions,
          acceptServiceDescription: null,
          acceptProblemDescription: null,
          acceptMaterials: {},
        })
      } catch (error) {
        showToast({ type: 'error', message: formatErrorMessage(error) })
      } finally {
        setValue('__aiLoading', false, { shouldDirty: false })
      }
    },
    [dispatch, validateInfoToAdjust, showToast, setValue, setAiSuggestionsModalInfo]
  )

  const aiButtonEnabled = useCallback(
    resolution => {
      return (
        Boolean(resolution?.serviceDescription) &&
        isBudgetValid &&
        !watch('__aiLoading') &&
        isEditing
      )
    },
    [budget?.isRevised, watch('__aiLoading'), isEditing, isBudgetValid]
  )

  function AIAdjustments({ item }) {
    if (!shouldShowAiAdjustButton) return null
    const resolutions = watch('budgetResolutions')
    const resolution = resolutions?.find(r => r.id === item?.id)

    const isDisabled = !aiButtonEnabled(resolution)

    return (
      <Tooltip title="Ajustar textos com a IA" placement="top">
        <IconButton
          disabled={isDisabled}
          onClick={() => handleAiAdjust(resolution)}
          onMouseDown={e => e.stopPropagation()}
        >
          <AutoFixHighIcon
            className={classNames(styles.aiIcon, {
              [styles.aiIconDisabled]: isDisabled,
            })}
          />
        </IconButton>
      </Tooltip>
    )
  }

  const TableHeader = useCallback(() => {
    if (!fields?.length) return null

    const fieldsWithError = {}

    errors?.budgetResolutions?.forEach(resolutionError => {
      if (resolutionError) {
        Object.keys(resolutionError).forEach(field => {
          fieldsWithError[field] = true
        })
      }
    })

    return (
      <Grid container className={styles.header} wrap="nowrap">
        {PROBLEMS_TABLE_ITEMS.map(header => {
          const showError = Boolean(fieldsWithError[header.field])
          if (header?.field === 'ai' && !shouldShowAiAdjustButton) return null
          return (
            <Grid item key={header.label} style={header.style}>
              <Typography
                variant="body2"
                className={styles.headerLabel}
                style={{
                  color: showError ? Theme.palette.error.main : undefined,
                }}
              >
                {header.label}
                {header.required && isEditing && (
                  <span style={{ color: Theme.palette.error.main, marginLeft: '4px' }}>*</span>
                )}
              </Typography>
            </Grid>
          )
        })}
      </Grid>
    )
  }, [fields, isEditing, errors, styles, shouldShowAiAdjustButton])

  const getMaterialsTotal = useCallback(
    resolutionIndex => {
      const materials = getValues(`budgetResolutions.${resolutionIndex}.materials`) || []

      return materials.reduce((acc, m) => {
        const t = Number(m?.total) || 0
        return acc + t
      }, 0)
    },
    [getValues]
  )

  const handleChange = useCallback(
    ({ field, value, indexPath }) => {
      const resolutionIndex = indexPath.split('.')[1]

      if (field === 'quantity' || field === 'unitPrice') {
        const quantity = field === 'quantity' ? value : Number(getValues(`${indexPath}.quantity`))
        const unitPrice =
          field === 'unitPrice' ? value : Number(getValues(`${indexPath}.unitPrice`))

        setValue(`${indexPath}.total`, quantity * unitPrice)

        const materialsTotal = getMaterialsTotal(resolutionIndex)
        const laborValue = Number(getValues(`budgetResolutions.${resolutionIndex}.laborValue`)) || 0

        setValue(`budgetResolutions.${resolutionIndex}.materialsTotal`, materialsTotal)
        setValue(`budgetResolutions.${resolutionIndex}.total`, materialsTotal + laborValue)

        return
      }

      if (field === 'laborValue') {
        const materialsTotal = getMaterialsTotal(resolutionIndex)
        setValue(`budgetResolutions.${resolutionIndex}.total`, materialsTotal + value)
      }
    },
    [getMaterialsTotal, getValues, setValue]
  )

  const handleBlur = async e => {
    const { name, value } = e.target

    if (!value) {
      setValue(name, 0)
    }
  }

  function GridTextField({ field, indexPath, isService = true, ...props }) {
    const fieldPath = `${indexPath}.${field}`
    const inputPropsStyle = props?.formatMoney ? { textAlign: 'right', paddingRight: '0.8rem' } : {}

    const fieldStyle = isService ? findProblemItemByField(field) : findMaterialItemByField(field)

    const disabledFields = ['total', 'materialsTotal']
    const watchedValue = watchedField(`${indexPath}.${field}`)

    useEffect(() => {
      if (props?.formatMoney) {
        handleChange({ field, value: watchedValue, indexPath })
      }
    }, [watchedValue, field, indexPath, handleChange, props.formatMoney])

    const required = ['problemDescription', 'serviceDescription'].includes(field)

    const fieldError = fieldPath?.split('.')?.reduce((acc, key) => acc?.[key], errors)

    if (isEditing && props?.formatMoney) {
      return (
        <Grid style={fieldStyle?.style}>
          <NumericInput
            name={fieldPath}
            placeholder="0,00"
            disabled={disabledFields.includes(field)}
            variant="standard"
            onBlur={handleBlur}
            wrapperStyle={{ width: '100%' }}
            defaultValue={props?.defaultValue ?? 0}
          />
        </Grid>
      )
    }

    return (
      <Grid style={fieldStyle?.style}>
        <Controller
          name={fieldPath}
          control={control}
          defaultValue={props?.defaultValue ?? (props?.formatMoney ? 0 : '')}
          rules={{
            required: { value: required, message: 'Esse campo é obrigatório' },
          }}
          render={({ value, name, onChange, onBlur }) => {
            const displayValue = isEditing ? value : props?.formatMoney ? formatMoney(value) : value
            return (
              <TextField
                name={name}
                value={displayValue}
                onChange={onChange}
                variant="standard"
                inputProps={{ style: inputPropsStyle }}
                multiline={isEditing}
                minRows={1}
                maxRows={3}
                error={!!fieldError && isEditing}
                style={{
                  width: '100%',
                  pointerEvents: isEditing ? 'auto' : 'none',
                }}
                InputProps={{
                  style: {
                    fontSize: '14px',
                    padding: '2px 0',
                    whiteSpace: 'pre-wrap',
                  },
                }}
                disabled={!isEditing || disabledFields.includes(field)}
                onBlur={onBlur}
                {...props}
              />
            )
          }}
        />
        {fieldError && isEditing && (
          <div style={{ marginTop: 4 }}>
            <ErrorMessage customErrorMessage={fieldError?.message} />
          </div>
        )}
      </Grid>
    )
  }

  const handleRowClick = useCallback(
    (event, item) => {
      if (isEditing) return
      event.stopPropagation()
      setAddProblemReadOnly(true)
      setBudgetResolutionToEdit(item)
      setPage('add-service')
    },
    [setPage, isEditing, setAddProblemReadOnly, setBudgetResolutionToEdit]
  )

  const TableMaterialHeader = useCallback(() => {
    const columns = MATERIAL_TABLE_ITEMS

    return (
      <Grid container className={styles.header} wrap="nowrap">
        {columns.map(header => (
          <Grid item key={header.label} style={header.style}>
            <Typography variant="body2" className={styles.headerLabel}>
              {header.label}
            </Typography>
          </Grid>
        ))}
      </Grid>
    )
  }, [fields, isEditing])

  const MaterialRow = useCallback(
    ({ resolutionIndex, rowIndex, row, showExclamation, priceConference }) => {
      return (
        <Grid container wrap="nowrap" className={styles.materialRow}>
          <GridTextField
            isService={false}
            field="description"
            indexPath={`budgetResolutions.${resolutionIndex}.materials.${rowIndex}`}
            defaultValue={row?.description}
          />
          <GridTextField
            isService={false}
            field="quantity"
            indexPath={`budgetResolutions.${resolutionIndex}.materials.${rowIndex}`}
            formatMoney
            defaultValue={row?.quantity || 0}
          />
          <GridTextField
            isService={false}
            field="unitPrice"
            indexPath={`budgetResolutions.${resolutionIndex}.materials.${rowIndex}`}
            formatMoney
            defaultValue={row?.unitPrice || 0}
          />
          <GridTextField
            isService={false}
            field="total"
            indexPath={`budgetResolutions.${resolutionIndex}.materials.${rowIndex}`}
            formatMoney
            defaultValue={row?.total || 0}
          />
          {showExclamation && rowIndex === 0 && (
            <div style={{ marginLeft: '8px', display: 'flex', alignItems: 'center' }}>
              <Tooltip
                title={<PriceConferenceTooltipContent data={priceConference} />}
                placement="top"
                classes={{ tooltip: styles.tooltip }}
              >
                <div style={{ display: 'flex' }}>
                  <Svg icon={InfoCircleIcon} />
                </div>
              </Tooltip>
            </div>
          )}
        </Grid>
      )
    },
    [isEditing]
  )

  function MaterialComponent({
    open,
    resolutionIndex,
    materials,
    showExclamation,
    priceConference,
  }) {
    return (
      <Collapse in={open} style={{ overflow: 'hidden' }}>
        <Grid className={styles.materialWrapper}>
          <TableMaterialHeader />

          {materials?.map((material, index) => (
            <MaterialRow
              key={material?.id}
              resolutionIndex={resolutionIndex}
              rowIndex={index}
              row={material}
              showExclamation={showExclamation}
              priceConference={priceConference}
            />
          ))}
        </Grid>
      </Collapse>
    )
  }

  const problemRow = useCallback(
    (item, resolutionIndex) => {
      const { warrantyMonths } = item
      const openMaterials = watch('__openMaterials') || {}

      const open = openMaterials[item.id] ?? isEditing

      const resolution = budgetResolutions?.[resolutionIndex]
      const showExclamation = resolution?.aiSuggestions?.priceConference?.length > 0
      const priceConference = resolution?.aiSuggestions?.priceConference
      return (
        <div style={{ width: '100%' }}>
          <Grid
            className={styles.problemRow}
            onMouseDown={event => handleRowClick(event, item)}
            sx={{
              zIndex: !isEditing && 4,

              '&:hover': {
                backgroundColor: !isEditing && Theme.Colors.Primary.Lightest,
                borderRadius: !isEditing && '8px',
                cursor: !isEditing && 'pointer',
              },
            }}
          >
            <GridTextField
              field="problemDescription"
              indexPath={`budgetResolutions.${resolutionIndex}`}
              defaultValue={item?.problemDescription}
            />

            <GridTextField
              field="serviceDescription"
              indexPath={`budgetResolutions.${resolutionIndex}`}
              defaultValue={item?.serviceDescription}
            />
            <GridTextField
              field="materialsTotal"
              formatMoney
              indexPath={`budgetResolutions.${resolutionIndex}`}
              defaultValue={item?.materialsTotal || 0}
            />
            <GridTextField
              field="laborValue"
              formatMoney
              indexPath={`budgetResolutions.${resolutionIndex}`}
              defaultValue={item?.laborValue || 0}
            />
            <GridTextField
              field="total"
              formatMoney
              indexPath={`budgetResolutions.${resolutionIndex}`}
              defaultValue={item?.total || 0}
            />
            <GridTextField
              field="warrantyMonths"
              defaultValue={isEditing ? warrantyMonths : handleWarrantyMonths(warrantyMonths)}
              indexPath={`budgetResolutions.${resolutionIndex}`}
            />
            <TypeChip isServiceMandatory={item?.isServiceMandatory} />
            <Grid display="flex" wrap="nowrap">
              <AIAdjustments item={item} />
              <ExpandMaterialButton open={open} item={item} />
              <EditProblemButton item={item} />
              <RemoveProblemButton resolutionIndex={resolutionIndex} />
            </Grid>
          </Grid>

          {/* Materiais do problema */}

          <MaterialComponent
            open={open}
            resolutionIndex={resolutionIndex}
            materials={item?.materials}
            showExclamation={showExclamation}
            priceConference={priceConference}
          />
        </div>
      )
    },
    [isEditing]
  )

  const showTradesmanDetailView = isTradesmanProposeView && tradesmanDetailItems.length > 0

  return (
    <Accordion id="problems-and-services" title="Problemas e Serviços" className={styles.accordion}>
      <Grid className={styles.wrapper}>
        {showTradesmanDetailView ? (
          <>
            <Grid className={styles.tradesmanDetailList}>
              <Grid>
                <Typography className={styles.tradesmanDetailDescription}>Descrição</Typography>
                {tradesmanDetailItems.map(item => (
                  <div key={item.id} className={styles.tradesmanDetailItem}>
                    {item.description}
                  </div>
                ))}
              </Grid>
              <Grid>
                <Typography className={styles.tradesmanDetailPrice}>R$</Typography>
                {tradesmanDetailItems.map(item => (
                  <div key={item.id} className={styles.tradesmanDetailItem}>
                    {formatMoney(item.price)}
                  </div>
                ))}
              </Grid>
            </Grid>
            <Grid className={styles.totalValueContainer}>
              <Typography className={styles.totalValueText}>
                Valor Total: {displayCurrency(displayTotalValue)}
              </Typography>
            </Grid>
          </>
        ) : (
          <>
            {fields?.length > 0 && (
              <Grid>
                <TableHeader />
                <DragAndDrop
                  id="problems-and-services-drag-and-drop"
                  list={fields}
                  renderItem={(item, resolutionIndex) =>
                    fields?.length > 0 ? problemRow(item, resolutionIndex) : null
                  }
                  updateList={updateRowPosition}
                  listProps={listProps}
                  itemProps={itemProps}
                  isDragDisabled={!isEditing}
                  dragIconClassName={styles.dragIcon}
                />
              </Grid>
            )}
            {fields?.length > 0 && (
              <Grid className={styles.totalValueContainer}>
                <Typography className={styles.totalValueText}>
                  Valor Total: {displayCurrency(displayTotalValue)}
                </Typography>
              </Grid>
            )}
            <AddServiceButton
              handleAddServiceClick={() =>
                checkUserPermission({
                  permission: PERMISSIONS.BUDGETS,
                  permissionAction: [PERMISSIONS_ACTIONS.ADD, PERMISSIONS_ACTIONS.EDIT],
                  functionAccess: handleAddServiceClick,
                })
              }
            />
          </>
        )}
      </Grid>
      {aiSuggestionsModalInfo.open && (
        <AiSuggestionsModal
          modalInfo={aiSuggestionsModalInfo}
          setModalInfo={setAiSuggestionsModalInfo}
          onClose={() => setAiSuggestionsModalInfo(AI_SUGGESTIONS_MODAL_INITIAL_STATE)}
        />
      )}
    </Accordion>
  )
}
